import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit3, Trash2, Search, Package } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const API =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

const formatBRL = (n) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    Number.isFinite(+n) ? +n : 0
  );

const ingredientSchema = z
  .object({
    id: z.string().trim().optional().or(z.literal("")),
    name: z
      .string({ required_error: "Nome é obrigatório" })
      .trim()
      .min(1, "Nome é obrigatório")
      .max(120, "Máx. 120 chars"),
    price: z
      .union([z.string(), z.number()])
      .transform((v) => (typeof v === "string" ? v.replace(",", ".") : v))
      .pipe(
        z.coerce
          .number({ invalid_type_error: "Preço deve ser numérico" })
          .min(0, "Preço deve ser ≥ 0")
      )
      .refine(
        (v) => Number.isFinite(v) && Math.round(v * 100) === v * 100,
        "Preço deve ter no máximo 2 casas decimais"
      ),
    visible: z.boolean().optional(),
  })
  .strict();

const defaultValues = { id: "", name: "", price: 0, visible: true };

const normalizeVisible = (value, fallback = false) => {
  if (typeof value === "string") {
    const normalized = value.trim().toUpperCase();
    if (normalized === "N") return false;
    if (normalized === "S") return true;
  }
  if (typeof value === "boolean") return value;
  if (value == null) return fallback;
  return Boolean(value);
};

export default function IngredientsManagement() {
  const [ingredients, setIngredients] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [fetching, setFetching] = useState(true);

  // estado do modal de confirmação de exclusão
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(ingredientSchema),
    defaultValues,
  });

  useEffect(() => {
    fetchIngredients();
  }, []);

  async function fetchIngredients() {
    setFetching(true);
    try {
      const res = await axios.get(`${API}/ingredients`);
      const list = res?.data?.data ?? [];
      setIngredients(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Error fetching ingredients:", e);
      setIngredients([]);
    } finally {
      setFetching(false);
    }
  }

  function openCreate() {
    setEditing(null);
    // novo ingrediente: checkbox deve ficar marcado por padrão
    reset(defaultValues);
    setShowDialog(true);
  }

  function openEdit(item) {
    setEditing(item);
    reset({
      id: String(item?.id ?? ""),
      name: item?.name ?? "",
      price: Number(item?.price ?? 0),
      // Regra de negócio: se o registro NÃO possui o atributo 'visible', o checkbox fica desmarcado;
      // caso contrário, reflete o valor do registro.
      visible: Object.prototype.hasOwnProperty.call(item, "visible")
        ? normalizeVisible(item.visible)
        : false,
    });
    setShowDialog(true);
  }

  async function onSubmit(formValues) {
    const isVisible = normalizeVisible(formValues.visible, true);
    const payload = {
      id: String(formValues.id).trim(),
      name: String(formValues.name).trim(),
      price: Number(formValues.price),
      visible: isVisible ? "S" : "N",
    };
    try {
      if (editing) {
        await axios.put(`${API}/ingredients/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/ingredients`, payload);
      }
      await fetchIngredients();
      setShowDialog(false);
      reset(defaultValues);
      setEditing(null);
    } catch (e) {
      console.error("Error saving ingredient:", e);
    }
  }

  // abrir modal de confirmação
  function askDelete(item) {
    setToDelete(item);
    setConfirmOpen(true);
  }

  async function confirmDelete() {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/ingredients/${toDelete.id}`);
      await fetchIngredients();
    } catch (e) {
      console.error("Error deleting ingredient:", e);
    } finally {
      setConfirmOpen(false);
      setToDelete(null);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ingredients;
    return ingredients.filter((i) => {
      const id = String(i.id || "").toLowerCase();
      const name = String(i.name || "").toLowerCase();
      return id.includes(q) || name.includes(q);
    });
  }, [ingredients, search]);

  return (
    <div className="space-y-6">
      {/* Modal de Loading */}
      <Dialog open={fetching} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#c9332C] rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Package className="h-6 w-6 text-[#c9332C]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              Carregando ingredientes...
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header + Ações */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h2 className="text-3xl font-bold text-gray-900">
          Gestão de Ingredientes
        </h2>

        <div className="flex flex-col-reverse gap-3 md:flex-row md:items-center">
          {/* Busca */}
          <div className="relative">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar por nome ou ID..."
              className="pl-9 w-72"
              aria-label="Pesquisar ingredientes"
            />
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          </div>

          {/* Botão Novo */}
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                className=""
                style={{ background: "#c9332C" }}
                onMouseEnter={(e) => (e.target.style.background = "#a02820")}
                onMouseLeave={(e) => (e.target.style.background = "#c9332C")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Ingrediente
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Editar Ingrediente" : "Novo Ingrediente"}
                </DialogTitle>
                <DialogDescription>
                  {editing
                    ? "Atualize os dados do ingrediente."
                    : "Adicione um ingrediente ao catálogo."}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* ID */}
                <div>
                  <Input
                    id="id"
                    disabled
                    value={editing?.id || ""}
                    {...register("id")}
                    className="bg-gray-100 cursor-not-allowed"
                  />
                  {errors.id && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.id.message}
                    </p>
                  )}
                </div>

                {/* Nome */}
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    placeholder="ex.: Granola Premium"
                    {...register("name")}
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Preço */}
                <div>
                  <Label htmlFor="price">Preço (R$)</Label>
                  <Input
                    id="price"
                    inputMode="decimal"
                    placeholder="ex.: 2.50"
                    {...register("price")}
                  />
                  {errors.price && (
                    <p className="text-red-600 text-sm mt-1">
                      {errors.price.message}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Use ponto para decimais (ex.: 2.50). Máx. 2 casas.
                  </p>
                </div>

                {/* Visible */}
                <Controller
                  name="visible"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
                      <div>
                        <Label className="text-sm font-medium">
                          Exibir no catálogo
                        </Label>
                        <p className="text-xs text-gray-500">
                          Desative para ocultar temporariamente o ingrediente.
                        </p>
                      </div>
                      <Switch
                        id="visible"
                        checked={Boolean(field.value)}
                        onCheckedChange={field.onChange}
                        aria-label="Alternar visibilidade"
                      />
                    </div>
                  )}
                />

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDialog(false)}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {editing ? "Atualizar" : "Criar"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Grid de cartões */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 min-h-[160px]">
        {!fetching && filtered.length === 0 ? (
          <div className="col-span-full text-center text-gray-500 py-10">
            {search
              ? "Nenhum ingrediente encontrado para a busca."
              : "Nenhum ingrediente cadastrado."}
          </div>
        ) : (
          filtered.map((ing) => {
            const isVisible = Object.prototype.hasOwnProperty.call(
              ing,
              "visible"
            )
              ? normalizeVisible(ing.visible, true)
              : true;
            return (
              <Card
                key={ing.id}
                className="overflow-hidden hover:shadow-lg transition-shadow"
              >
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">{ing.name}</h3>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          isVisible
                            ? "border-emerald-200 text-emerald-700"
                            : "border-gray-300 text-gray-500"
                        }
                      >
                        {isVisible ? "Visível" : "Oculto"}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        ID: {ing.id}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Preço</span>
                    <span className="font-bold">{formatBRL(ing.price)}</span>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(ing)}
                    >
                      <Edit3 className="h-4 w-4 mr-1" />
                      Editar
                    </Button>

                    {/* Botão que abre o modal de confirmação */}
                    <AlertDialog
                      open={confirmOpen && toDelete?.id === ing.id}
                      onOpenChange={(o) => {
                        // se usuário fechar diretamente, limpar referência
                        setConfirmOpen(o);
                        if (!o) setToDelete(null);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => askDelete(ing)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Excluir
                        </Button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Excluir ingrediente?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita. O ingrediente{" "}
                            <span className="font-semibold">
                              {toDelete?.name}
                            </span>{" "}
                            (ID: {toDelete?.id}) será removido do catálogo.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel onClick={() => setToDelete(null)}>
                            Cancelar
                          </AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-600 hover:bg-red-700"
                            onClick={confirmDelete}
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
